import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import PageTitle from "../../layouts/PageTitle";
import { getUpdateProfileList } from "../../../services/userApiService/userService";
import { Search, Calendar, ChevronDown, UserPen, Landmark } from "lucide-react";

const UserLogs = () => {

    const [logs, setLogs] = useState({});
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Unified state
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('Last 7 Days');
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const filterOptions = ['Last 7 Days', 'Last Month', 'All Time'];

    const getStatusValue = (filterName) => {
        switch (filterName) {
            case 'Last Month': return 'month';
            case 'All Time': return 'all';
            default: return 'last7days';
        }
    };


    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getUpdateProfileList({
                number: 10,
                page: currentPage,
                search: searchQuery,
                filter: getStatusValue(selectedFilter)
            });
            if (res.data.status) {
                setLogs(res.data.data);
                // Total pages would ideally come from API, but for now using a dummy or calculating if possible.
                // If the API returns pagination metadata, use that.
                setTotalPages(res.data.data.last_page || 1);
            }
        } catch (error) {
            console.error("Error fetching logs:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchQuery, selectedFilter]);

    useEffect(() => {
        const delaySearch = setTimeout(() => {
            fetchLogs();
        }, 500);
        return () => clearTimeout(delaySearch);
    }, [fetchLogs]);

    const getIcon = (title) => {
        switch (title?.toLowerCase()) {
            case "login":
            case "login attempt":
            case "logout":
                return <i className="bi bi-box-arrow-in-right fs-4"></i>;
            case "password change":
                return <i className="bi bi-lock fs-4"></i>;
            case "session revoked":
                return <i className="bi bi-power fs-4"></i>;
            case "profile update":
                return <UserPen strokeWidth={1} />
            case "bank detail update":
                return <Landmark strokeWidth={1} />;
            default:
                return <i className="bi bi-info-circle fs-4"></i>;
        }
    };

    const getDescription = (item) => {
        if (item.title === "Profile Update" || item.title === "Bank Detail Update" || item.title === "Sport Detail Update" || item.title === "Medical Detail Update"||item.title === "Update All Details") {
            if (item.updated_column && item.updated_column.length > 0) {
                return `User updated ${item.updated_column.join(", ")}.`;
            }
            return `User updated ${item.title.toLowerCase()}.`;
        }
        return item.message || "Activity recorded.";
    };


    return (
        <div className="container-fluid">
            <PageTitle activeMenu="Logs" motherMenu="Logs" />

            <div className="card">
                <div className="card-header border-0 pb-0 d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center flex-grow-1 border rounded-3 px-3 py-2" style={{ maxWidth: '700px' }}>
                        <Search className="w-5 h-5 text-muted me-2" size={20} />
                        <input
                            type="text"
                            placeholder="Search Log..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="bg-transparent border-0 outline-none flex-grow-1  text-dark placeholder-muted"
                            style={{ outline: 'none', boxShadow: 'none' }}
                        />
                    </div>
                    <div className="position-relative">
                        <button
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className="d-flex align-items-center gap-2 px-4 py-2 border rounded-3 bg-white text-dark hover-light"
                            style={{ cursor: 'pointer' }}
                        >
                            <Calendar className="w-4 h-4" size={16} />
                            <span>{selectedFilter}</span>
                            <ChevronDown className="w-4 h-4" size={16} />
                        </button>

                        {isFilterOpen && (
                            <div className="position-absolute end-0 mt-2 bg-white rounded-3 shadow border border-light py-1 z-3" style={{ width: '12rem', zIndex: 1050 }}>
                                {filterOptions.map((option) => (
                                    <button
                                        key={option}
                                        onClick={() => {
                                            setSelectedFilter(option);
                                            setIsFilterOpen(false);
                                        }}
                                        className={`w-full text-start px-3 py-2  border-0 bg-transparent d-block w-100 ${selectedFilter === option ? 'text-primary bg-light' : 'text-dark hover-bg-light'
                                            }`}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="card-body">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="activity-timeline">
                            {Object.keys(logs).length > 0 ? (
                                Object.entries(logs).map(([date, items]) => (
                                    <div key={date}>
                                        <div className="d-flex align-items-center mb-4 mt-2">
                                            <div className="flex-grow-1 border-top"></div>
                                            <span className="mx-3 text-black fs-5  text-uppercase">
                                                {moment(date).format("DD MMM, YYYY")}
                                            </span>
                                            <div className="flex-grow-1 border-top"></div>
                                        </div>
                                        {items.map((log, idx) => (
                                            <div key={idx} className="d-flex mb-4 align-items-start">
                                                <div className="activity-icon rounded  border-light  p-3 me-3 d-flex align-items-center justify-content-center" style={{ minWidth: '55px', backgroundColor: '#f9fafb' }}>
                                                    {getIcon(log.title)}
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h4 className="mb-1 ">{log.title}</h4>
                                                    <p className="text-muted mb-0 ">{getDescription(log)}</p>
                                                </div>
                                                <div className="text-end ms-3" style={{ minWidth: '150px' }}>
                                                    <p className="mb-0  text-muted fw-medium">
                                                        {moment(log.updated_at).fromNow()}, {moment(log.updated_at).format("hh:mm A")}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-5">
                                    <p className="text-muted">No logs found</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default UserLogs;
